import { useEffect, useMemo, useState } from "react";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getCareerPaths, subscribeCareerPaths } from "../../data/careerPaths";
import {
  getQAItems, subscribeQA, itemPaths, addQA, updateQA, deleteQA, reorderQA, exportQA, importQA, resetQA, newQAItem, type QAItem,
} from "../../data/qa";
import { logAudit } from "../lib/audit";
import { RichTextEditor } from "../../components/RichTextEditor";

type Draft = Omit<QAItem, "id" | "order"> & Partial<Pick<QAItem, "id" | "order">>;

/** Career-path options + bilingual labels for the department selector. */
function usePathOptions(): { options: string[]; labels: Record<string, { ar: string; en: string }> } {
  const [paths, setPaths] = useState(() => getCareerPaths());
  useEffect(() => subscribeCareerPaths(() => setPaths([...getCareerPaths()])), []);
  return useMemo(() => {
    const options = ["all", ...paths.map((p) => p.id)];
    const labels: Record<string, { ar: string; en: string }> = { all: { ar: "جميع الأقسام", en: "All Departments" } };
    for (const p of paths) labels[p.id] = { ar: p.ar, en: p.en };
    return { options, labels };
  }, [paths]);
}

function QAEditModal({ lang, item, isNew, onClose, onSave }: { lang: Lang; item: Draft; isNew: boolean; onClose: () => void; onSave: (v: Draft) => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [draft, setDraft] = useState<Draft>(item);
  const { options, labels } = usePathOptions();
  const set = (key: keyof Draft, val: any) => setDraft((d) => ({ ...d, [key]: val }));

  function save() {
    if (!draft.questionAr.trim() || !draft.questionEn.trim()) {
      alert(t("يرجى تعبئة السؤال بالعربية والإنجليزية", "Please fill the question in both Arabic and English"));
      return;
    }
    onSave(draft);
  }

  const selected = itemPaths(draft as QAItem);
  return (
    <div className="kd-admin-modal-backdrop" onClick={onClose}>
      <div className="kd-admin-modal kd-admin-modal-wide" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="kd-admin-modal-header">
          <h2>{isNew ? t("إضافة سؤال جديد", "Add New Question") : t("تعديل السؤال", "Edit Question")}</h2>
          <button className="kd-admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="kd-admin-modal-body">
          <div>
            <label style={{ display: "block", marginBottom: 6 }}><span>{t("الأقسام المستهدفة (يمكن اختيار أكثر من قسم)", "Target Departments (multi-select)")}</span></label>
            <div className="kd-admin-paths-grid">
              {options.map((opt) => {
                const checked = selected.includes(opt);
                return (
                  <label className="kd-admin-checkbox kd-admin-path-chip" key={opt}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        let next: string[];
                        if (opt === "all") next = e.target.checked ? ["all"] : [];
                        else {
                          const rest = selected.filter((s) => s !== "all" && s !== opt);
                          next = e.target.checked ? [...rest, opt] : rest;
                          if (next.length === 0) next = ["all"];
                        }
                        set("paths", next);
                        set("path", next[0] || "all");
                      }}
                    />
                    <span>{lang === "ar" ? labels[opt]?.ar : labels[opt]?.en}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <label className="kd-admin-checkbox" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set("enabled", e.target.checked)} />
            <span>{t("السؤال مفعّل (يظهر للموظفين)", "Question enabled (visible to employees)")}</span>
          </label>
          <label><span>{t("السؤال بالعربية", "Question (Arabic)")}</span><input type="text" value={draft.questionAr} onChange={(e) => set("questionAr", e.target.value)} dir="rtl" /></label>
          <label><span>{t("الإجابة بالعربية", "Answer (Arabic)")}</span></label>
          <RichTextEditor value={draft.answerHtmlAr} onChange={(html) => set("answerHtmlAr", html)} dir="rtl" placeholder={t("اكتب الإجابة هنا...", "Type the answer here...")} />
          <label><span>{t("السؤال بالإنجليزية", "Question (English)")}</span><input type="text" value={draft.questionEn} onChange={(e) => set("questionEn", e.target.value)} dir="ltr" /></label>
          <label><span>{t("الإجابة بالإنجليزية", "Answer (English)")}</span></label>
          <RichTextEditor value={draft.answerHtmlEn} onChange={(html) => set("answerHtmlEn", html)} dir="ltr" placeholder="Type the answer here..." />
        </div>
        <div className="kd-admin-modal-footer">
          <button className="kd-btn-secondary" onClick={onClose}>{t("إلغاء", "Cancel")}</button>
          <button className="kd-btn-primary kd-btn-gold" onClick={save}>{t("حفظ", "Save")}</button>
        </div>
      </div>
    </div>
  );
}

/** Department Q&A editor tab (ut) — table + add/edit modal + import/export/reset. */
export function QATab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [items, setItems] = useState<QAItem[]>(() => getQAItems());
  const [editing, setEditing] = useState<{ item: Draft; isNew: boolean } | null>(null);
  const [filter, setFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const { options, labels } = usePathOptions();
  useEffect(() => subscribeQA(() => setItems([...getQAItems()])), []);

  const shown = filter ? items.filter((q) => itemPaths(q).includes(filter)) : items;

  async function onSave(v: Draft) {
    if (editing?.isNew) {
      const created = await addQA(v as any);
      logAudit("qa_added", `${created.path}: ${created.questionAr.slice(0, 60)}`);
    } else {
      await updateQA((v as QAItem).id, v);
      logAudit("qa_updated", `${v.path}: ${v.questionAr.slice(0, 60)}`);
    }
    setEditing(null);
  }
  function del(q: QAItem) {
    if (confirm(t("سيتم حذف هذا السؤال نهائياً. متأكد؟", "This question will be permanently deleted. Are you sure?"))) {
      deleteQA(q.id);
      logAudit("qa_deleted", `${q.path}: ${q.questionAr.slice(0, 60)}`);
    }
  }
  function exportJson() {
    const blob = new Blob([exportQA()], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "kaden-qa-config.json";
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async function doImport() {
    if (await importQA(importText)) {
      setImportOpen(false);
      setImportText("");
      logAudit("config_imported", "Q&A items imported");
      alert(t("تم الاستيراد بنجاح", "Imported successfully"));
    } else alert(t("ملف غير صالح", "Invalid file"));
  }
  function reset() {
    if (confirm(t("استعادة الأسئلة الافتراضية وحذف كل التعديلات؟", "Reset to default questions and discard all changes?"))) resetQA();
  }

  return (
    <div className="kd-admin-qa">
      <div className="kd-admin-info-banner">
        <strong>{t("ملاحظة:", "Note:")}</strong>{" "}
        {t('الأسئلة تظهر في الخطوة الرابعة للموظفين بحسب القسم المختار. اختر "جميع الأقسام" لتظهر للجميع.', 'Questions appear in Step 4 to employees based on their selected department. Choose "All Departments" to show to everyone.')}
      </div>
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={() => setEditing({ item: newQAItem(), isNew: true })}>+ {t("إضافة سؤال جديد", "Add New Question")}</button>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="kd-admin-filter">
          <option value="">{t("كل الأقسام", "All departments")}</option>
          {options.map((o) => <option value={o} key={o}>{lang === "ar" ? labels[o]?.ar : labels[o]?.en}</option>)}
        </select>
        <button className="kd-btn-secondary" onClick={exportJson}>↓ {t("تصدير", "Export")}</button>
        <button className="kd-btn-secondary" onClick={() => setImportOpen(true)}>↑ {t("استيراد", "Import")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset defaults")}</button>
      </div>
      <div className="kd-admin-table-wrap">
        <table className="kd-admin-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th><th>{t("السؤال", "Question")}</th><th>{t("القسم", "Department")}</th>
              <th style={{ width: 100 }}>{t("مفعّل", "Enabled")}</th><th style={{ width: 160 }}>{t("إجراءات", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: "2rem", color: "var(--kd-text-muted)" }}>{t('لا توجد أسئلة. اضغط "إضافة سؤال جديد".', 'No questions. Click "Add New Question".')}</td></tr>}
            {shown.map((q) => (
              <tr key={q.id}>
                <td>{q.order}</td>
                <td>
                  <div><strong>{lang === "ar" ? q.questionAr : q.questionEn}</strong></div>
                  <small style={{ color: "var(--kd-text-muted)" }}>{lang === "ar" ? q.questionEn : q.questionAr}</small>
                </td>
                <td>
                  <div className="kd-admin-path-tags">
                    {itemPaths(q).map((p) => <span className="kd-admin-path-tag" key={p}>{lang === "ar" ? labels[p]?.ar : labels[p]?.en}</span>)}
                  </div>
                </td>
                <td>
                  <label className="kd-admin-switch">
                    <input type="checkbox" checked={q.enabled} onChange={(e) => updateQA(q.id, { enabled: e.target.checked })} />
                    <span className="kd-admin-switch-slider" />
                  </label>
                </td>
                <td>
                  <div className="kd-admin-actions-cell">
                    <button className="kd-admin-action-btn" onClick={() => reorderQA(q.id, "up")} title="Up">↑</button>
                    <button className="kd-admin-action-btn" onClick={() => reorderQA(q.id, "down")} title="Down">↓</button>
                    <button className="kd-admin-action-btn" onClick={() => setEditing({ item: q, isNew: false })} title="Edit">✎</button>
                    <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => del(q)} title="Delete">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <QAEditModal lang={lang} item={editing.item} isNew={editing.isNew} onClose={() => setEditing(null)} onSave={onSave} />}
      {importOpen && (
        <div className="kd-admin-modal-backdrop" onClick={() => setImportOpen(false)}>
          <div className="kd-admin-modal" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="kd-admin-modal-header">
              <h2>{t("استيراد أسئلة", "Import Questions")}</h2>
              <button className="kd-admin-modal-close" onClick={() => setImportOpen(false)}>×</button>
            </div>
            <div className="kd-admin-modal-body">
              <label><span>{t("الصق محتوى ملف JSON:", "Paste JSON content:")}</span><textarea rows={10} value={importText} onChange={(e) => setImportText(e.target.value)} /></label>
            </div>
            <div className="kd-admin-modal-footer">
              <button className="kd-btn-secondary" onClick={() => setImportOpen(false)}>{t("إلغاء", "Cancel")}</button>
              <button className="kd-btn-primary kd-btn-gold" onClick={doImport}>{t("استيراد", "Import")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
